import React, { useState } from 'react';
import uuid from 'uuid-random';
import { ButtonSize, ButtonVariant, Spinner } from 'react-magma-dom';
import { CloudDownloadIcon, PlayArrowIcon } from 'react-magma-icons';
import { Image } from '../Image/Image';
import { VideoModal } from '../Modal/VideoModal';
import { ErrorDownloadModal } from '../Modal/ErrorDownloadModal';
import { StyledPlayVideoButton, MarginWrapper, StyledRelative, RelatedWrapper } from '../Styled';
import { StyledButtonSpinnerWrapper } from '../Styled/StyledWrappers';
import { StyledDownloadButton } from '../Styled/StyledButtons';
import {
  StyledContainer,
  StyledBlock,
  StyledLinkWrapper,
  StyledSubTitle,
  StyledContentWrapper,
  StyledLeftSideLinks,
  StyledProfessionalSeparator,
  StyledItemData,
  StyledTypeData,
  EllipsisWrapper,
  StyledOpacityWrapper,
  StyledMainGradientType,
  StyledRightSideWrapper,
} from '../Styled/StyledRelatedNav';
import { getVideoId, handleDownloadFile } from '../../utils';
import { COLORS, RELATED_ITEM_WIDTH, SIZES } from '../../constants';
import { IAsset } from '../../constants/assets';

interface IRelatedItem {
  asset: IAsset;
  index: number;
}

export const RelatedItem = (props: IRelatedItem): JSX.Element => {
  const { asset, index } = props;

  const {
    title,
    type,
    description,
    thumbnail,
    links: { self: fileLink },
  } = asset;

  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const openErrorModal = () => {
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onDownload = () => {
    !isLoading && handleDownloadFile(title, fileLink, setIsLoading, openErrorModal);
  };

  const renderSideElement = () => {
    if (type === 'Video') {
      return (
        <MarginWrapper data-testid="video-item" margin="16px 20px 16px 22.2px">
          <StyledRelative>
            <StyledPlayVideoButton
              aria-label={`Play ${title} video`}
              icon={<PlayArrowIcon />}
              onClick={() => setShowModal(true)}
            />
            <StyledOpacityWrapper opacity={0.5}>
              <Image src={thumbnail} alt={''} width={SIZES.S} height={SIZES.XS} />
            </StyledOpacityWrapper>
          </StyledRelative>
        </MarginWrapper>
      );
    }

    return (
      <StyledRightSideWrapper>
        <RelatedWrapper>
          {isLoading ? (
            <StyledButtonSpinnerWrapper className="file-download-loader" margin="12px" disabled={isLoading}>
              <Spinner color={COLORS.seaBlue} size={36} />
            </StyledButtonSpinnerWrapper>
          ) : (
            <StyledDownloadButton
              testId="download-btn"
              onClick={async () => {
                if (type !== 'Video') onDownload();
              }}
              size={ButtonSize.large}
              aria-label={`Download ${type} ${title}`}
              variant={ButtonVariant.link}
              icon={<CloudDownloadIcon size={40} />}
            />
          )}
          <StyledMainGradientType>PDF</StyledMainGradientType>
        </RelatedWrapper>
      </StyledRightSideWrapper>
    );
  };

  return (
    <>
      {index === 0 && <MarginWrapper data-testid="margin-wrapper-block" margin="46px" />}
      <StyledContainer testId="related-item" key={uuid()}>
        <StyledBlock width={RELATED_ITEM_WIDTH}>
          <StyledContentWrapper>
            <StyledSubTitle>{title}</StyledSubTitle>
            <StyledLinkWrapper justifyContent="space-between">
              <StyledLeftSideLinks>
                <StyledLeftSideLinks>
                  <StyledItemData>
                    <StyledTypeData>{type === 'PDF' ? 'Resource' : type}</StyledTypeData>
                  </StyledItemData>
                  <StyledProfessionalSeparator />
                  <StyledItemData>
                    <EllipsisWrapper>{description}</EllipsisWrapper>
                  </StyledItemData>
                </StyledLeftSideLinks>
              </StyledLeftSideLinks>
            </StyledLinkWrapper>
          </StyledContentWrapper>
          {renderSideElement()}
        </StyledBlock>
      </StyledContainer>
      <VideoModal id={getVideoId(asset.links.self)} showModal={showModal} setShowModal={setShowModal} />
      {isErrorModalOpen && (
        <ErrorDownloadModal
          mainColor={COLORS.seaBlue}
          onCloseClick={closeErrorModal}
          onRetryClick={() => {
            closeErrorModal();
            onDownload();
          }}
        />
      )}
    </>
  );
};
